import { ClerkLoaded, ClerkLoading } from "@clerk/nextjs";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import type { PropsWithChildren } from "react";

import LoadingAnimation from "~/components/LoadingAnimation";
import NavbarUserMenu from "~/components/NavbarUserMenu";

export interface SecondaryLayoutProps extends PropsWithChildren<{}> {
	isLoading?: boolean;
	className?: string;
	homeUrl?: string;
}

const SecondaryLayout = ({
	children,
	isLoading,
	className,
	homeUrl,
}: SecondaryLayoutProps) => {
	return (
		<>
			<ClerkLoading>
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{
						opacity: 0,
						transition: {
							delay: 0.4,
						},
					}}
					className="-mt-28 flex w-full flex-1 items-center justify-center"
					key="loading"
				>
					<LoadingAnimation />
				</motion.div>
			</ClerkLoading>

			<ClerkLoaded>
				<div
					className={clsx(
						`relative flex min-h-screen flex-col bg-polka`,
						className,
					)}
				>
					<div className="flex w-full justify-center px-10 py-8 sm:justify-between">
						<Link href={homeUrl ?? "/"}>
							<div className="relative z-20 h-10 w-64">
								<Image
									priority
									fetchPriority="high"
									src="https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/brookbush-institute-logo-UrZkEAxVZrvVZLcrMzNhIsAuLU3HFQ.webp"
									alt="Brookbush Institute Logo"
									fill
									className="object-contain dark:hidden"
								/>

								<Image
									priority
									fetchPriority="high"
									src="https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/brookbush-institute-logo-dark-Ut9JNc4SoFgiwgKuUIV0wM5CxEwK77.webp"
									alt="Brookbush Institute Logo Dark"
									fill
									className="hidden object-contain dark:block"
								/>
							</div>
						</Link>

						<div className="z-20 hidden sm:block">
							<NavbarUserMenu />
						</div>
					</div>

					<AnimatePresence mode="wait">
						{isLoading ? (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{
									opacity: 0,
									transition: {
										delay: 0.4,
									},
								}}
								className="-mt-28 flex w-full flex-1 items-center justify-center"
								key="loading"
							>
								<LoadingAnimation />
							</motion.div>
						) : (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{
									opacity: 1,
									transition: {
										duration: 0.4,
									},
								}}
								className="-mt-0 flex w-full justify-center px-3 pb-8 sm:-mt-28 sm:flex-1 sm:items-center sm:py-28"
								exit={{ opacity: 0 }}
								key="content"
							>
								{children}
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</ClerkLoaded>
		</>
	);
};

export default SecondaryLayout;
